/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState} from 'react'
import {defineMessage, FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'

import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    Heading,
    SimpleGrid,
    Skeleton,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import FormActionButtons from '../../components/forms/form-action-buttons'
import {useForm} from 'react-hook-form'
import {useToast} from '../../hooks/use-toast'
import useShipToCountry from '../../hooks/use-ship-to-country'

import LoadingSpinner from '../../components/loading-spinner'
import {PinIcon} from '../../components/custom-icons'
import {PlusIcon} from '@app/components/icons'
import ActionCard from '../../components/action-card'
import AddressFields from '../../components/forms/address-fields'
import AddressDisplay from '../../components/address-display'
import PageActionPlaceHolder from '../../components/page-action-placeholder'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {nanoid} from 'nanoid'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'

// Phone utils
import {findDialCodeByCountry, extractPhone} from './../../utils/utils'
import {REGEX} from '../../constants'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'

const DEFAULT_SKELETON_COUNT = 3

/**
 * BoxArrow is a React functional component that renders a styled box element.
 *
 * This component returns a `Box` element with specific styling properties applied
 * to create an arrow-like appearance. The box is positioned absolutely and rotated
 * to achieve the desired visual effect.
 *
 * @returns {JSX.Element} A JSX element representing the styled box.
 *
 * Styling Details:
 * - Width and Height: 3 units each.
 * - Borders: Left and top borders are solid with a color of blue.600.
 * - Positioning: Positioned absolutely with the left side at 50% and bottom at -31px.
 * - Z-Index: Set to 1 to ensure proper stacking order.
 * - Background: White.
 * - Transform: Rotated by 45 degrees to create an arrow shape.
 */
const BoxArrow = () => {
    return (
        <Box
            width={3}
            height={3}
            borderLeft="1px solid"
            borderTop="1px solid"
            borderColor="darkGray"
            position="absolute"
            left="50%"
            bottom="-31px"
            zIndex={1}
            background="white"
            transform="rotate(45deg)"
        />
    )
}

/**
 * ShippingAddressForm is a React functional component that renders a form for adding or editing
 * a shipping address. It conditionally displays different UI elements based on the presence of
 * existing addresses and the current form state.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.form - The form object containing form state and handlers.
 * @param {boolean} props.hasAddresses - A flag indicating whether the user has existing addresses.
 * @param {string} props.selectedAddressId - The ID of the address being edited, if any.
 * @param {Function} props.toggleEdit - A function to toggle the edit mode of the form.
 * @param {Function} props.submitForm - A function to handle form submission.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * The component uses a Box to wrap the form elements, applying different styles if the user
 * has existing addresses. It displays a loading spinner when the form is submitting and shows
 * an error alert if there are global form errors. The form includes address fields and action
 * buttons for submission and cancellation.
 */
const ShippingAddressForm = ({form, hasAddresses, selectedAddressId, toggleEdit, submitForm}) => {
    return (
        <Box
            position="relative"
            {...(hasAddresses && {
                gridColumn: ['span 1', 'span 1', 'span 2', 'span 3', 'span 3'],
                padding: ['24px 16px', '24px 16px', '24px 50px'],
                border: '1px solid',
                borderColor: 'darkGray',
                bgColor: 'white',
                my: [8, 8, 0]
            })}
        >
            {form.formState.isSubmitting && (
                <LoadingSpinner wrapperStyles={{backgroundColor: 'rgba(255,255,255,0.5)'}} />
            )}
            <Stack spacing={24}>
                <Heading as="h3" size="md">
                    <Text variant={'eyeBrowSmall'} textTransform={'uppercase'}>
                        {selectedAddressId ? (
                            <FormattedMessage
                                defaultMessage="Edit Address"
                                id="shipping_address_form.heading.edit_address"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Add New Address"
                                id="shipping_address_form.heading.new_address"
                            />
                        )}
                    </Text>
                </Heading>
                <Box>
                    <form onSubmit={form.handleSubmit(submitForm)} noValidate>
                        <Stack spacing={24}>
                            {form.formState.errors?.global && (
                                <Alert status="error">
                                    <AlertIcon color="red" boxSize={4} />
                                    <Text fontSize="sm" ml={3}>
                                        {form.formState.errors.global.message}
                                    </Text>
                                </Alert>
                            )}
                            <AddressFields form={form} />
                            <FormActionButtons onCancel={toggleEdit} />
                        </Stack>
                    </form>
                </Box>
            </Stack>
        </Box>
    )
}

ShippingAddressForm.propTypes = {
    form: PropTypes.object,
    hasAddresses: PropTypes.bool,
    selectedAddressId: PropTypes.string,
    toggleEdit: PropTypes.func,
    submitForm: PropTypes.func
}

const successfullyAddedAddress = defineMessage({
    defaultMessage: 'New address saved',
    id: 'account_addresses.info.new_address_saved'
})

const successfullyUpdatedAddress = defineMessage({
    defaultMessage: 'Address updated',
    id: 'account_addresses.info.address_updated'
})

const successfullyRemovedAddress = defineMessage({
    defaultMessage: 'Address removed',
    id: 'account_addresses.info.address_removed'
})

/**
 * AccountAddresses is a React functional component that manages and displays a user's saved addresses
 * within their account page. It provides functionality for adding, editing, and removing addresses,
 * and ensures accessibility by managing focus appropriately.
 *
 * This component utilizes several hooks and context providers:
 * - `useIntl`: For internationalization and formatting messages.
 * - `useCurrentCustomer`: To fetch the current customer's data, including their addresses.
 * - `useShopperCustomersMutation`: To perform mutations related to customer addresses, such as creating,
 *   updating, and removing addresses.
 * - `useState`: To manage local component state, including editing states and selected address IDs.
 * - `useToast`: To display toast notifications for success and error messages.
 * - `useForm`: To handle form state and validation.
 *
 * The component renders a list of addresses in a grid format, with options to add a new address or edit
 * existing ones. It displays skeleton loaders while data is loading and provides feedback through toast
 * notifications upon successful or failed operations.
 *
 * Accessibility is enhanced by focusing on the 'Addresses' header when the component mounts and managing
 * focus on edit buttons and form elements during interactions.
 *
 * @returns {JSX.Element} A React component that renders the account addresses management UI.
 */
const AccountAddresses = () => {
    const {formatMessage} = useIntl()
    const {data: customer, isLoading} = useCurrentCustomer()
    const {isRegistered, customerId} = customer
    const [addresses, setAddresses] = useState([])
    const {usePageLoadAndCookies} = useGTM()

    useEffect(() => {
        if (!isLoading) {
            setAddresses(customer.addresses)
        }
    }, [isLoading])

    const addCustomerAddress = useShopperCustomersMutation('createCustomerAddress')
    const updateSavedAddress = useShopperCustomersMutation('updateCustomerAddress')
    const removeCustomerAddress = useShopperCustomersMutation('removeCustomerAddress')

    const [isEditing, setIsEditing] = useState(false)
    const [selectedAddressId, setSelectedAddressId] = useState(false)
    const showToast = useToast()
    const form = useForm()
    const {shipToCountry} = useShipToCountry()
    const defaultValues = {
        address1: '',
        address2: '',
        city: '',
        countryCode: shipToCountry?.id,
        firstName: '',
        lastName: '',
        phone: '',
        stateCode: '',
        postalCode: '',
        preferred: false,
        c_phoneCountry: '',
        c_fullAddress: ''
    }

    // keep track of the edit buttons so we can focus on them later for accessibility
    const [editBtnRefs, setEditBtnRefs] = useState({})
    useEffect(() => {
        const currentRefs = {}
        addresses?.forEach(({addressId}) => {
            currentRefs[addressId] = React.createRef()
        })
        setEditBtnRefs(currentRefs)
    }, [addresses])

    const hasAddresses = addresses?.length > 0

    /**
     * Displays an error toast notification to the user.
     *
     * This function triggers a toast message with a predefined error title
     * and sets the status of the toast to 'error'. It utilizes the `showToast`
     * function to render the notification and `formatMessage` to format the
     * error message title using the `API_ERROR_MESSAGE` constant.
     *
     * @function
     */
    const showError = () => {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    }

    /**
     * Submits the address form by either updating an existing address or adding a new one.
     *
     * This asynchronous function handles form submission for address management. It determines
     * whether to update an existing address or add a new one based on the presence of a
     * `selectedAddressId`. The function clears any existing form errors, removes unnecessary
     * fields from the address object, and then performs the appropriate API call to either
     * update or add the address. Upon successful completion, it toggles the edit mode and
     * displays a success toast notification. If an error occurs during the process, it sets
     * a global form error with the error message.
     *
     * @async
     * @function
     * @param {Object} address - The address object containing the details to be submitted.
     * @throws Will set a form error if the API call fails.
     */
    const submitForm = async (address) => {
        try {
            let data
            form.clearErrors()

            // address phone
            const {phone, phoneCountry} = address
            const dialCode = phone ? findDialCodeByCountry(phoneCountry) : ''

            address.phone = phone ? `${dialCode} ${phone}`.trim() : ''
            address.c_phoneCountry = phone ? phoneCountry : ''

            delete address.phoneCountry

            if (selectedAddressId) {
                const body = {
                    ...address,
                    addressId: selectedAddressId
                }
                data = await updateSavedAddress.mutateAsync({
                    body,
                    parameters: {
                        customerId,
                        addressName: selectedAddressId
                    }
                })
            } else {
                const body = {
                    addressId: nanoid(),
                    ...address
                }
                data = await addCustomerAddress.mutateAsync({
                    body,
                    parameters: {customerId: customer.customerId}
                })
            }
            if (data) {
                let index = 0
                let updatedAddresses = addresses

                if (updatedAddresses) {
                    index = updatedAddresses?.findIndex((addr) => addr.addressId === data.addressId)
                    updatedAddresses[index] = data
                } else {
                    updatedAddresses = [data]
                }
                if (data.preferred) {
                    updatedAddresses?.forEach((addr) => {
                        addr.preferred = addr.addressId === data.addressId
                    })
                    if (index >= 0) {
                        updatedAddresses.unshift(updatedAddresses.splice(index, 1)[0])
                    } else {
                        updatedAddresses.unshift(data)
                    }
                } else {
                    if (!index) {
                        setAddresses(updatedAddresses)
                    }
                    if (index < 0) {
                        updatedAddresses.push(data)
                    }
                }
                setAddresses(updatedAddresses)
                toggleEdit()
                showToast({
                    title: selectedAddressId
                        ? formatMessage(successfullyUpdatedAddress)
                        : formatMessage(successfullyAddedAddress),
                    status: 'success'
                })
            }
        } catch (error) {
            form.setError('global', {type: 'manual', message: error.message})
        }
    }

    /**
     * Asynchronously removes a customer address by its ID and updates the UI accordingly.
     *
     * This function handles the removal of a customer address from the system. It checks if the address
     * being removed is the currently selected address, and if so, it resets the selection and editing state.
     * It then calls an asynchronous mutation to remove the address from the backend. Upon successful removal,
     * a success toast notification is displayed, and focus is moved to the header for better accessibility.
     *
     * @async
     * @function removeAddress
     * @param {string} addressId - The unique identifier of the address to be removed.
     * @throws Will throw an error if the address removal fails.
     */
    const removeAddress = async (addressId) => {
        try {
            if (addressId === selectedAddressId) {
                setSelectedAddressId(undefined)
                setIsEditing(false)
                form.reset()
            }
            await removeCustomerAddress.mutateAsync(
                {
                    parameters: {
                        customerId,
                        addressName: addressId
                    }
                },
                {
                    onSuccess: () => {
                        setAddresses(addresses.filter((addr) => addr.addressId !== addressId))
                        showToast({
                            title: formatMessage(successfullyRemovedAddress),
                            status: 'success'
                        })
                    }
                }
            )
        } catch (error) {
            showError()
            throw error
        }
    }

    /**
     * Toggles the edit state for a given address in the address management form.
     *
     * This function resets the form with the provided address data and updates the editing state.
     * If the address has an `addressId`, it sets the selected address ID and enables editing mode.
     * Otherwise, it focuses on the edit button that opened the form when the form closes, or on the
     * heading if the button cannot be found. It then clears the selected address ID and toggles the
     * editing state.
     *
     * @param {Object} address - The address object to be edited.
     * @param {string} [address.addressId] - The unique identifier of the address, if available.
     */
    const toggleEdit = (address) => {
        form.reset(defaultValues)

        if (address?.addressId) {
            const extractedPhone = extractPhone(REGEX.PHONE, address.phone)?.phone

            // Reset the form with the selected address values, including the extracted phone number
            form.reset({
                ...address,
                phone: extractedPhone
            })

            setSelectedAddressId(address.addressId)
            setIsEditing(true)
        } else {
            // Focus on the edit button that opened the form when the form closes
            const focusAfterClose = editBtnRefs[selectedAddressId]?.current
            focusAfterClose?.focus()
            setSelectedAddressId(undefined)
            setIsEditing(!isEditing)
        }
    }

    usePageLoadAndCookies('account')

    return (
        <Stack spacing={24} data-testid="account-addresses-page">
            <Box>
                <Heading
                    as="h1"
                    display="inline-block"
                    size="3xl"
                    textTransform="uppercase"
                    tabIndex="0"
                >
                    <FormattedMessage
                        defaultMessage="Addresses"
                        id="account_addresses.title.addresses"
                    />
                </Heading>
            </Box>

            {isLoading && (
                <SimpleGrid columns={[1, 1, 2, 3, 3]} spacing={4}>
                    {new Array(DEFAULT_SKELETON_COUNT).fill().map((_, index) => {
                        return (
                            <ActionCard key={`skeleton-${index}`}>
                                <Stack spacing={2} marginBottom={7}>
                                    <Skeleton height="23px" width="120px" />

                                    <Skeleton height="23px" width="84px" />

                                    <Skeleton height="23px" width="104px" />
                                </Stack>
                            </ActionCard>
                        )
                    })}
                </SimpleGrid>
            )}

            {hasAddresses && (
                <SimpleGrid
                    columns={[1, 1, 2, 3, 3]}
                    spacing={[16, 16, 24]}
                    gridAutoFlow="row dense"
                >
                    {
                        <Button
                            variant="outline"
                            border="1px dashed"
                            borderColor="darkGray"
                            height={{lg: 'full'}}
                            minHeight={11}
                            fontWeight="medium"
                            textTransform={'uppercase'}
                            leftIcon={<PlusIcon display="block" boxSize={'15px'} />}
                            onClick={() => toggleEdit()}
                        >
                            <FormattedMessage
                                defaultMessage="Add Address"
                                id="account_addresses.button.add_address"
                            />
                            {isEditing && !selectedAddressId && <BoxArrow />}
                        </Button>
                    }

                    {isEditing && !selectedAddressId && (
                        <ShippingAddressForm
                            form={form}
                            hasAddresses={hasAddresses}
                            submitForm={submitForm}
                            selectedAddressId={selectedAddressId}
                            toggleEdit={toggleEdit}
                        />
                    )}

                    {addresses.map((address) => {
                        const editLabel = formatMessage(
                            {
                                defaultMessage: 'Edit {address}',
                                id: 'shipping_address.label.edit_button'
                            },
                            {address: address.address1}
                        )

                        const removeLabel = formatMessage(
                            {
                                defaultMessage: 'Remove {address}',
                                id: 'shipping_address.label.remove_button'
                            },
                            {address: address.address1}
                        )

                        return (
                            <React.Fragment key={address.addressId}>
                                <ActionCard
                                    borderColor="borderGray"
                                    bg="white"
                                    p={'16px 24px'}
                                    key={address.addressId}
                                    editBtnRef={editBtnRefs[address.addressId]}
                                    onRemove={() => removeAddress(address.addressId)}
                                    onEdit={() => toggleEdit(address)}
                                    editBtnLabel={editLabel}
                                    removeBtnLabel={removeLabel}
                                >
                                    {address.preferred && (
                                        <Badge
                                            fontSize="xs"
                                            variant="solid"
                                            bg="lightBeige"
                                            color={'black'}
                                            textTransform={'uppercase'}
                                            p={4}
                                            mb={8}
                                        >
                                            <FormattedMessage
                                                defaultMessage="Default"
                                                id="account_addresses.badge.default"
                                            />
                                        </Badge>
                                    )}
                                    <AddressDisplay address={address} mb={'16'} />
                                    {isEditing && address.addressId === selectedAddressId && (
                                        <BoxArrow />
                                    )}
                                </ActionCard>

                                {isEditing && address.addressId === selectedAddressId && (
                                    <ShippingAddressForm
                                        form={form}
                                        hasAddresses={hasAddresses}
                                        submitForm={submitForm}
                                        selectedAddressId={selectedAddressId}
                                        toggleEdit={toggleEdit}
                                    />
                                )}
                            </React.Fragment>
                        )
                    })}
                </SimpleGrid>
            )}

            {!hasAddresses && !isLoading && (
                <Box layerStyle="accountCard">
                    {!isEditing && isRegistered && (
                        <PageActionPlaceHolder
                            icon={<PinIcon boxSize={8} />}
                            heading={formatMessage({
                                defaultMessage: 'No Saved Addresses',
                                id: 'account_addresses.page_action_placeholder.heading.no_saved_addresses'
                            })}
                            text={formatMessage({
                                defaultMessage: 'Add a new address method for faster checkout.',
                                id: 'account_addresses.page_action_placeholder.message.add_new_address'
                            })}
                            buttonText={formatMessage({
                                defaultMessage: 'Add Address',
                                id: 'account_addresses.page_action_placeholder.button.add_address'
                            })}
                            buttonProps={{
                                variant: 'primary'
                            }}
                            onButtonClick={() => toggleEdit()}
                        />
                    )}
                    {isEditing && !selectedAddressId && (
                        <ShippingAddressForm
                            form={form}
                            hasAddresses={hasAddresses}
                            submitForm={submitForm}
                            selectedAddressId={selectedAddressId}
                            toggleEdit={toggleEdit}
                        />
                    )}
                </Box>
            )}
        </Stack>
    )
}

AccountAddresses.getTemplateName = () => 'account-addresses'

export default AccountAddresses
